import { formatDateWithTimezone } from '@doss/common-all'
import { Box, Grid, Stack, Theme, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import useStyles from '../../common/hooks/useStyles'
import { DetailedTableRow } from '../tables/types'
import { Modal } from '../ui/molecules/modal/Modal'
import { RecordDetailTableRow } from './types'

interface RecordPropertiesDialogProps {
  tableRow: DetailedTableRow | RecordDetailTableRow
  openDialog: boolean
  handleCloseRecordProperties: () => void
}

function getStyles(theme: Theme) {
  return {
    container: {
      padding: theme.spacing(2),
      borderRadius: '2px',
      backgroundColor: theme.palette.common.doss.gray[1],
      border: `1px solid ${theme.palette.common.doss.gray[4]}`,
    },
    title: {
      color: theme.palette.text.secondary,
    },
    value: {
      wordBreak: 'break-word',
      whiteSpace: 'normal',
    },
  }
}

/**
 * Shows a page containing properties / metadata of a record
 */
export function RecordPropertiesDialog({
  tableRow,
  openDialog,
  handleCloseRecordProperties,
}: RecordPropertiesDialogProps) {
  const styles = useStyles(getStyles)
  const { t } = useTypedTranslation()

  const recordProperties = [
    {
      title: t('record.properties.updated_at'),
      description: formatDateWithTimezone(dayjs(tableRow.updatedAt), 'ddd, MM/DD/YY [at] hh:mma'),
    },
    {
      title: t('record.properties.updated_by'),
      description: tableRow.updatedBy
        ? `${tableRow.updatedBy.firstName} ${tableRow.updatedBy.lastName}`
        : t('common.unknown'),
    },
    {
      title: t('record.properties.created_at'),
      description: formatDateWithTimezone(dayjs(tableRow.createdAt), 'ddd, MM/DD/YY [at] hh:mma'),
    },
    {
      title: t('record.properties.created_by'),
      description: tableRow.createdBy
        ? `${tableRow.createdBy.firstName} ${tableRow.createdBy.lastName}`
        : t('common.unknown'),
    },
  ]

  return (
    <Modal
      fullWidth
      onClose={handleCloseRecordProperties}
      open={openDialog}
      title={t('record.properties.properties')}
    >
      <Box sx={styles.container}>
        <Stack spacing={1.25}>
          {recordProperties.map((property, index) => (
            <Grid key={index} alignItems="baseline" columnSpacing={2} container>
              <Grid item md={3} sm={4} xs={12}>
                <Typography sx={styles.title} variant="body2">
                  {property.title}
                </Typography>
              </Grid>
              <Grid item md={9} sm={8} xs={12}>
                <Typography sx={styles.value} variant="body2">
                  {property.description}
                </Typography>
              </Grid>
            </Grid>
          ))}
        </Stack>
      </Box>
    </Modal>
  )
}
